<template>
	<div :id="uid" :variant="variant" class="search" :style="widthStyle">
		<Label
			:for="uid"
			class="search__label"
			v-if="label"
			:labelText="label"
		/>
		<div class="search__input-container" ref="dropdownPosRef">
			<input
				autocomplete="off"
				class="search__input"
				name="search"
				ref="search"
				type="text"
				:aria-label="label"
				:disabled="disabled"
				:id="`${uid}_input`"
				:placeholder="placeholder"
				:value="decodeURIComponent(modelValue)"
				@input="updateValue"
				@keydown.enter="emitInputSearch"
				@keypress.enter.prevent
				v-focus
			/>
			<span :class="['search__btn', hasSpinnerLoading && 'has-spinner']">
				<Button
					v-if="displayClearSearch"
					className="btn--icon-only"
					:btnClick="clearSearch"
					:disabled="disabled"
					:iconBeforeLabel="true"
					iconClassName="icon-close"
					ref="clearBtnRef"
				/>
				<Button
					className="btn--icon-only"
					:btnClick="clickSearchButton"
					:disabled="disabled"
					:iconBeforeLabel="true"
					:iconClassName="iconClass"
				/>
			</span>
		</div>
		<Autosuggest
			v-if="displayAutosuggest"
			ref="dropdownListRef"
			@selected="autosuggestSelected"
			:searchQuery="modelValue"
			:options="autosuggestOptions"
			:hideGroupsLabel="autosuggestHideGroupsLabel"
		/>
		<PopperPosition
			trackedRefName="dropdownPosRef"
			popoverRefName="dropdownListRef"
			placement="bottom-start"
			:popperVisible="displayAutosuggest"
			strategy="absolute"
			@popperMinWidthChanged="setpopperMinWidth"
			@positionUpdated="updateIconOrientation"
		/>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import Label from '@/components/forms/label/label';
import Autosuggest from '@/components/forms/autosuggest/autosuggest.vue';

// mixins
import PopperPosition from '@/components/visual/popper-position';
import popperPositionMixin from '@/mixins/popper-position-mixin';
import DocumentClick from '@/mixins/document-click';

const I_SEARCH = 'icon-search';
const I_CLOSE = 'icon-close';

export default {
	name: 'Search',
	directives: {
		focus: {
			inserted: function(el) {
				el.focus();
			}
		}
	},
	components: {
		Label,
		Button,
		Autosuggest,
		PopperPosition
	},
	mixins: [popperPositionMixin, DocumentClick('hide')],
	model: {
		prop: 'modelValue',
		event: 'update:modelValue'
	},
	props: {
		label: {
			type: String,
			default: ''
		},
		placeholder: {
			type: String,
			default: ''
		},
		modelValue: {
			default: ''
		},
		timeout: {
			type: Number,
			default: 2000
		},
		width: {
			type: [String, Number],
			default: ''
		},
		setFocus: {
			type: Boolean,
			default: true
			//:TODO: pending implementation
		},
		enableTimeOut: {
			type: Boolean,
			default: true
		},
		autosuggest: {
			type: Boolean,
			default: false
		},
		autosuggestOptions: {
			type: Array,
			default: () => []
		},
		autosuggestHideGroupsLabel: Boolean
	},
	data() {
		return {
			inputValue: this.modelValue,
			hasSpinnerLoading: false, ///DEV: this shoud be set to true when the button spinner is used ///,
			displayAutosuggest: false,
			displayClearSearch: false
		};
	},
	computed: {
		/*
		listeners() {
			return {
				...this.$listeners,
				keyup: this.updateValue
			};
		}, */
		iconClass() {
			if (this.autosuggest) {
				return I_SEARCH;
			}

			if (this.modelValue) return I_CLOSE;
			return I_SEARCH;
		},
		widthStyle() {
			if (typeof this.width === 'number') {
				return {
					width: `${this.width}px`
				};
			}
			return {
				width: this.width
			};
		}
	},
	methods: {
		// handle with a ref from outside the component
		visible() {
			this.displayAutosuggest = true;
			this.$emit('autosuggest-visible');
		},
		hide() {
			this.displayAutosuggest = false;
			this.$emit('autosuggest-hidden');
		},
		clearOn() {
			this.displayClearSearch = true;
		},
		clearOff() {
			this.displayClearSearch = false;
		},
		autosuggestSelected(value) {
			this.inputValue = value.textValue;
			// this.emitValue(this.inputValue);
			this.$emit('update:modelValue', value.textValue);
			this.autosuggestSelection();
		},
		emitValue(value) {
			this.$emit('change', value);
		},
		updateValue(e) {
			this.inputValue = e.target.value;
			this.$emit('enterKey', false);
			//enter key event
			if (e.keyCode !== 13) {
				//delay the @keyup event
				if (this.timer) {
					clearTimeout(this.timer);
					this.timer = null;
				}

				if (this.enableTimeOut && this.timeout) {
					this.timer = setTimeout(() => {
						this.$emit('update:modelValue', this.inputValue);
						this.$emit('change', e.target.value);
					}, this.timeout);
				} else {
					this.$emit('update:modelValue', this.inputValue);
					this.$emit('change', e.target.value);
				}
			}
		},
		emitEvents() {
			this.$emit('update:modelValue', this.inputValue);
			this.$emit('change', this.inputValue);
		},
		emitInputSearch(e) {
			if (this.timer) {
				clearTimeout(this.timer);
				this.timer = null;
			}

			this.$refs.search.focus();

			this.$emit('enterKey', true);

			if (this.autosuggest) {
				this.autosuggestSelection();
			} else {
				this.emitValue(e.target.value);
				this.$emit('update:modelValue', e.target.value);
			}
		},
		clearSearch() {
			this.$emit('update:modelValue', '');
			this.$emit('autosuggest-selection', '');
			this.hide();
			this.clearOff();
		},
		clickSearchButton(e) {
			if (e) {
				e.preventDefault();
			}

			if (this.timer) {
				clearTimeout(this.timer);
				this.timer = null;
			}

			this.$refs.search.focus();
			if (this.iconClass === I_SEARCH) {
				if (this.autosuggest) {
					this.autosuggestSelection();
				} else {
					this.emitValue(this.inputValue);
					this.$emit('update:modelValue', this.inputValue);
				}
			} else {
				this.inputValue = '';
				this.$emit('update:modelValue', '');
				this.emitValue('');
				this.hide();
			}
		},
		autosuggestSelection() {
			this.$emit('autosuggest-selection', this.$refs.search.value);
			this.hide();
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Search styles
    * Can use SCSS variables = TRUE
*/

@import '@/scss/5-objects/_form.scss';

.search {
	position: relative;

	&__input-container {
		display: flex;
		position: relative;
		@include textfield;

		&:focus-within {
			-o-outline: $focus-outline;
			-moz-outline: $focus-outline;
			outline: $focus-outline;
			outline-offset: $focus-offset;
		}

		.search__btn.has-spinner {
			.btn--icon-only {
				min-width: auto;
				margin-right: $gutter-sm;
			}
		}
	}

	&__input {
		flex-grow: 2;
		appearance: none;
		outline: none;
		border: none;
		cursor: pointer;
		font-size: $input-font-size;
		text-overflow: ellipsis;
		background-color: transparent;
		border-radius: $form-border-radius;
		padding: $input-padding-y $input-padding-x;
		@include input-height;
	}

	&__btn {
		display: flex;
		align-items: center;
		min-width: 20px;
		user-select: none;
		cursor: pointer;
		outline: 0;
		border: none;
		background-color: transparent;

		.btn--icon-only {
			min-width: 32px;
		}
	}
}
</style>
